import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ChevronLeft, ChevronRight, Loader2, Moon, Users, Heart, Send } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const shift = (ym, d) => { const [y, m] = ym.split("-").map(Number); const dt = new Date(Date.UTC(y, m - 1 + d, 1)); return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}`; };
const monthName = (ym) => new Date(`${ym}-01T12:00:00`).toLocaleDateString("en-NZ", { month: "long", year: "numeric" });
const longDate = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("en-NZ", { weekday: "long", day: "numeric", month: "long" });
const STYLE = {
  available: "bg-[var(--dahlias-forest)] text-white", partly: "bg-[#C9A227] text-[#3C2A1E]", full: "bg-[#3E1F46] text-white",
  event: "bg-[var(--berry-deep)] text-white", closed: "bg-[#9C8B73] text-white", sold_out: "bg-[#A33030] text-white",
};
const LABEL = { available: "Available", partly: "Partly available", full: "Reserved", event: "Event", closed: "Closed", sold_out: "Sold out" };

/** Calendar-first booking: month grid on the page, tap a day for its sessions, Buddy sessions and date requests. */
export function BookingCalendar({ onPickSlot, onPickBuddy, selectedId, buddyMode, onBuddyModeChange }) {
  const [month, setMonth] = useState("");
  const [data, setData] = useState(null);
  const [sel, setSel] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    setErr("");
    axios.get(`${API}/calendar`, { params: month ? { month } : {} })
      .then((r) => { setData(r.data); if (!month) setMonth(r.data.month); })
      .catch(() => setErr("The calendar couldn't load — please try again in a moment."));
  }, [month]);

  // "Find a Buddy Booking": jump to the first month with an open pair place.
  useEffect(() => {
    if (!buddyMode || !data) return;
    if ((data.days || []).some((d) => d.has_buddy)) return;
    const next = shift(data.month, 1);
    if (Number(next.slice(0, 4)) > Number(data.month.slice(0, 4)) + 1) return;
    setMonth(next);
  }, [buddyMode, data]);

  const byDate = useMemo(() => Object.fromEntries((data?.days || []).map((d) => [d.date, d])), [data]);
  const grid = useMemo(() => {
    if (!data) return [];
    const [y, m] = data.month.split("-").map(Number);
    const lead = (new Date(Date.UTC(y, m - 1, 1)).getUTCDay() + 6) % 7;
    const days = new Date(Date.UTC(y, m, 0)).getUTCDate();
    const cells = Array.from({ length: lead }, () => null);
    for (let i = 1; i <= days; i++) cells.push(`${data.month}-${String(i).padStart(2, "0")}`);
    return cells;
  }, [data]);
  const day = sel ? byDate[sel] : null;
  const bookable = day && ((day.slots || []).some((s) => s.status === "available") || (day.buddy || []).some((b) => b.status === "available"));

  return (
    <div data-testid="booking-calendar" className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => month && setMonth(shift(month, -1))} aria-label="Previous month" data-testid="cal-prev"
          className="h-11 w-11 grid place-items-center rounded-full border-2 border-[var(--berry)] text-[var(--berry)]"><ChevronLeft className="w-5 h-5" /></button>
        <h3 className="font-display text-2xl w-56 text-center text-[var(--dahlias-forest)]" data-testid="cal-month">{month ? monthName(month) : "…"}</h3>
        <button type="button" onClick={() => month && setMonth(shift(month, 1))} aria-label="Next month" data-testid="cal-next"
          className="h-11 w-11 grid place-items-center rounded-full border-2 border-[var(--berry)] text-[var(--berry)]"><ChevronRight className="w-5 h-5" /></button>
        {buddyMode && (
          <button type="button" onClick={() => onBuddyModeChange(false)} data-testid="cal-buddy-mode-off"
            className="ml-auto inline-flex items-center gap-1.5 px-3 h-9 wobble-pill bg-[var(--berry)] text-white text-xs font-label tracking-[0.2em] uppercase">
            <Heart className="w-3.5 h-3.5" /> Showing Buddy dates · show all
          </button>
        )}
      </div>
      <ul className="flex flex-wrap gap-2 text-[11px]" data-testid="cal-legend">
        {Object.entries(LABEL).map(([k, l]) => <li key={k} className={`px-2 py-1 rounded-full ${STYLE[k]}`}>{l}</li>)}
        <li className="px-2 py-1 rounded-full border-2 border-[var(--berry)] text-[var(--berry)] inline-flex items-center gap-1"><Heart className="w-3 h-3" /> Buddy Booking</li>
      </ul>
      {err && <p className="text-sm text-[#A33030]" data-testid="cal-error">{err}</p>}
      {!data && !err && <p className="text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading the season…</p>}
      {data && (
        <div className="grid grid-cols-7 gap-1 sm:gap-1.5" data-testid="cal-grid">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="text-[10px] tracking-[0.2em] uppercase text-center text-[var(--dahlias-ink)]/50">{d}</div>)}
          {grid.map((iso, i) => {
            if (!iso) return <div key={`e${i}`} />;
            const info = byDate[iso];
            const buddyOpen = (info?.buddy || []).filter((b) => b.status === "available");
            const dim = buddyMode && buddyOpen.length === 0;
            const hasInfo = info && info.availability !== "none";
            return (
              <button key={iso} type="button" onClick={() => setSel(iso)} data-testid={`cal-day-${iso}`}
                className={"min-h-[60px] sm:min-h-[76px] rounded border-2 text-left p-1.5 transition-colors " +
                  (sel === iso ? "border-[var(--berry)] bg-white" : buddyOpen.length && buddyMode ? "border-[var(--berry)] bg-[var(--berry)]/10" : "border-[var(--berry)]/15 bg-white/70 hover:bg-white") +
                  (dim ? " opacity-35" : "")}>
                <div className="text-xs font-semibold text-[var(--dahlias-ink)]">{Number(iso.slice(-2))}</div>
                {hasInfo && <span className={`mt-1 inline-block px-1.5 py-0.5 rounded-full text-[9px] ${STYLE[info.availability] || STYLE.available}`}>{LABEL[info.availability] || "Available"}</span>}
                {buddyOpen.length > 0 && <div className="mt-0.5 text-[9px] text-[var(--berry)] font-semibold inline-flex items-center gap-0.5" data-testid={`cal-buddy-${iso}`}><Heart className="w-2.5 h-2.5" /> {buddyOpen[0].remaining} pair place{buddyOpen[0].remaining === 1 ? "" : "s"}</div>}
              </button>
            );
          })}
        </div>
      )}

      {sel && (
        <div className="bg-white border-[3px] border-[var(--berry)]/50 p-4 md:p-5 space-y-3" data-testid="cal-day-panel">
          <h4 className="font-display text-2xl text-[var(--dahlias-forest)]">{longDate(sel)}</h4>
          {(day?.slots || []).length > 0 && (
            <div>
              <div className="font-label tracking-[0.3em] text-[10px] text-[var(--dahlias-ink)]/60 mb-1.5">PRIVATE PICKING — THE FARM IS YOURS</div>
              <div className="flex flex-wrap gap-2">
                {day.slots.map((s) => (
                  <button key={s.id} type="button" disabled={s.status !== "available"} onClick={() => onPickSlot(s, sel)} data-testid={`cal-slot-${s.id}`}
                    className={`px-4 h-11 wobble-pill text-base inline-flex items-center gap-1.5 transition-colors disabled:opacity-40 ${selectedId === s.id
                      ? "bg-[var(--berry)] text-white" : "bg-white text-[var(--dahlias-ink)] border border-[var(--berry)]/30"}`}>
                    {s.evening && <Moon className="w-4 h-4" />} {s.time} <span className="text-xs opacity-70">· {s.status === "available" ? `from NZ$${s.price_from}` : "Reserved"}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {(day?.buddy || []).length > 0 && (
            <div>
              <div className="font-label tracking-[0.3em] text-[10px] text-[var(--berry)] mb-1.5 inline-flex items-center gap-1"><Heart className="w-3 h-3" /> BUDDY BOOKING — MADE FOR TWO</div>
              <div className="flex flex-wrap gap-2">
                {day.buddy.map((b) => (
                  <button key={b.id} type="button" disabled={b.status !== "available"} onClick={() => onPickBuddy({ ...b, date: sel })} data-testid={`cal-buddy-session-${b.id}`}
                    className={`px-4 h-11 wobble-pill text-base inline-flex items-center gap-1.5 transition-colors disabled:opacity-40 ${selectedId === b.id
                      ? "bg-[var(--berry)] text-white" : "bg-white text-[var(--dahlias-ink)] border-2 border-[var(--berry)]/60"}`}>
                    <Users className="w-4 h-4" /> {b.time}–{b.end}
                    <span className="text-xs opacity-80">· {b.status === "available" ? `${b.remaining} of ${b.max_pairs} pair places left · NZ$${b.price} for two` : b.status === "full" ? "All pair places taken" : "Not available"}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {(day?.events || []).filter((e) => e.type === "public_event").map((e) => (
            <a key={e.slug} href={`/visit/open-days/${e.slug}`} className="block text-sm underline text-[var(--berry-deep)]">★ {e.title}</a>
          ))}
          {!bookable && <RequestDate date={sel} />}
        </div>
      )}
    </div>
  );
}

/** "Request this date" — saved as a pending request for admin approval; never a confirmed booking. */
function RequestDate({ date }) {
  const [form, setForm] = useState({ name: "", email: "", group_size: 4, preferred_time: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return toast.error("We need your name and email.");
    setBusy(true);
    try {
      await axios.post(`${API}/calendar/request`, {
        preferred_date: date, preferred_time: form.preferred_time, group_size: Number(form.group_size) || 1,
        event_type: "private_picking", name: form.name, contact: form.email, notes: "Requested from the booking calendar.",
      });
      setSent(true); toast.success("Request sent — we'll be in touch.");
    } catch (er) { toast.error(er?.response?.data?.detail || "Couldn't send that request."); }
    finally { setBusy(false); }
  };
  if (sent) return (
    <p className="text-base text-[var(--dahlias-forest)] bg-[var(--dahlias-cream)] p-3" data-testid="request-date-sent">
      Thanks — your request for {longDate(date)} is with us. <strong>This is not a booking yet:</strong> we'll check the day and email you to approve or suggest another time. No payment is taken now.
    </p>
  );
  return (
    <form onSubmit={submit} className="bg-[var(--dahlias-cream)] p-4 space-y-3" data-testid="request-date-form">
      <div>
        <div className="font-display text-xl text-[var(--dahlias-forest)]">No bookable session on this day — request it</div>
        <p className="text-sm text-[var(--dahlias-ink)]/75 mt-1">Tell us who's coming and we'll see what we can do. <strong>A request needs our approval before it becomes a booking</strong> — nothing is confirmed or charged until we reply.</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" data-testid="request-name" className="h-11 px-3 border border-[var(--berry)]/30 bg-white text-base" />
        <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" data-testid="request-email" className="h-11 px-3 border border-[var(--berry)]/30 bg-white text-base" />
        <label className="text-xs text-[var(--dahlias-ink)]/70">Group size
          <input type="number" min="1" max="60" value={form.group_size} onChange={(e) => setForm({ ...form, group_size: e.target.value })} data-testid="request-size" className="mt-1 w-full h-11 px-3 border border-[var(--berry)]/30 bg-white text-base" /></label>
        <label className="text-xs text-[var(--dahlias-ink)]/70">Preferred time
          <input value={form.preferred_time} onChange={(e) => setForm({ ...form, preferred_time: e.target.value })} placeholder="e.g. mid-morning or 4pm" data-testid="request-time" className="mt-1 w-full h-11 px-3 border border-[var(--berry)]/30 bg-white text-base" /></label>
      </div>
      <button type="submit" disabled={busy} data-testid="request-submit" className="h-11 px-5 wobble-pill bg-[var(--dahlias-forest)] text-white font-display text-base inline-flex items-center gap-2 disabled:opacity-40">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Request this date
      </button>
    </form>
  );
}
